"use client";

import { normalPdf } from "@/lib/stats/distributions";
import { CHART_COLORS } from "@/lib/tool-kit/palette";

const WIDTH = 640;
const PAD_X = 28;
const CURVE_TOP = 22;
const BASE_Y = 150;
const ROW_HEIGHT = 20;
const AXIS_GAP = 16;
const STEPS = 160;
const Z_RANGE = 4;
const PEAK = normalPdf(0);

export interface BellShade {
  /** z-range to fill, clipped to the plotted range of −4 to 4. */
  from: number;
  to: number;
  color?: string;
  opacity?: number;
}

export interface BellTick {
  z: number;
  text: string;
}

export interface BellRow {
  label: string;
  ticks: readonly BellTick[];
}

/**
 * The standard normal curve in z units, with shaded z-ranges, an optional marker and any
 * number of labelled axis rows beneath (z, T, IQ, or the reader's own mean ± k SD).
 * The shading is also described in the accessible `label`, so no information lives in
 * colour alone.
 */
export function BellCurve({
  shades = [],
  marker,
  rows,
  label,
}: {
  shades?: readonly BellShade[];
  marker?: { z: number; label: string };
  rows: readonly BellRow[];
  label: string;
}) {
  const x = (z: number) =>
    PAD_X +
    ((Math.max(-Z_RANGE, Math.min(Z_RANGE, z)) + Z_RANGE) / (2 * Z_RANGE)) *
      (WIDTH - 2 * PAD_X);
  const y = (z: number) =>
    BASE_Y - (normalPdf(z) / PEAK) * (BASE_Y - CURVE_TOP);
  const axisY = BASE_Y + AXIS_GAP;
  const height = axisY + rows.length * ROW_HEIGHT + 14;

  const points = (from: number, to: number) => {
    const lo = Math.max(-Z_RANGE, from);
    const hi = Math.min(Z_RANGE, to);
    return Array.from(
      { length: STEPS + 1 },
      (_, i) => lo + ((hi - lo) * i) / STEPS
    );
  };
  const outline = points(-Z_RANGE, Z_RANGE)
    .map((z) => `${x(z)},${y(z)}`)
    .join(" ");

  return (
    <svg
      aria-label={label}
      className="h-auto w-full text-muted-foreground"
      role="img"
      viewBox={`0 0 ${WIDTH} ${height}`}
    >
      {shades.map((shade, index) => {
        if (shade.to <= shade.from) {
          return null;
        }
        const run = points(shade.from, shade.to);
        const first = Math.max(-Z_RANGE, shade.from);
        const last = Math.min(Z_RANGE, shade.to);
        const path = `M${x(first)},${BASE_Y} ${run.map((z) => `L${x(z)},${y(z)}`).join(" ")} L${x(last)},${BASE_Y} Z`;
        return (
          <path
            d={path}
            fill={shade.color ?? CHART_COLORS[0]}
            fillOpacity={shade.opacity ?? 0.3}
            // Shades are positional and may overlap.
            // biome-ignore lint/suspicious/noArrayIndexKey: positional shades
            key={index}
          />
        );
      })}
      <polyline
        fill="none"
        points={outline}
        stroke="currentColor"
        strokeWidth={2}
      />
      <line
        stroke="currentColor"
        x1={PAD_X}
        x2={WIDTH - PAD_X}
        y1={BASE_Y}
        y2={BASE_Y}
      />
      {marker ? (
        <g>
          <line
            stroke={CHART_COLORS[3]}
            strokeWidth={2.5}
            x1={x(marker.z)}
            x2={x(marker.z)}
            y1={CURVE_TOP - 6}
            y2={BASE_Y}
          />
          <text
            fill="currentColor"
            fontSize={12}
            fontWeight={600}
            textAnchor="middle"
            x={Math.min(WIDTH - 60, Math.max(60, x(marker.z)))}
            y={CURVE_TOP - 10}
          >
            {marker.label}
          </text>
        </g>
      ) : null}
      {rows.map((row, rowIndex) => (
        <g key={row.label}>
          <text
            fill="currentColor"
            fontSize={10}
            textAnchor="start"
            x={2}
            y={axisY + rowIndex * ROW_HEIGHT + 14}
          >
            {row.label}
          </text>
          {row.ticks.map((tick) => (
            <text
              fill="currentColor"
              fontSize={11}
              key={tick.z}
              textAnchor="middle"
              x={x(tick.z)}
              y={axisY + rowIndex * ROW_HEIGHT + 14}
            >
              {tick.text}
            </text>
          ))}
        </g>
      ))}
    </svg>
  );
}
